import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, IconButton, Box, Stepper, Step, StepLabel, Tabs, Tab} from '@mui/material'
import {ArrowBack, CheckCircle} from '@mui/icons-material'
import {clientApi, accountApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate, Link} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {parseError, phoneRegex, emailRegex} from '../../utils/helpers'
import {useMobile} from '../../hooks'
import {PersonalInformation, Contact, Preferences, AdditionalNotes} from './components'
import {format} from 'date-fns'

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const [activeStep, setActiveStep] = useState(0)
    const [nextLabel, setNextLabel] = useState('Next')
    const navigate = useNavigate()
    const {mobile} = useMobile()
    const [personalInformationData, setPersonalInformationData] = useState(null)
    const [contactData, setContactData] = useState(null)
    const [preferencesData, setPreferencesData] = useState(null)
    const [additionalNotesData, setAdditionalNotesData] = useState(null)
    const [avatarUrl, setAvatarUrl] = useState(null)
    const [file, setFile] = useState(null)
    const [branches, setBranches] = useState([])
    const [selectedBranches, setSelectedBranches] = useState([])
    const steps = ['Personal Information', 'Contact', 'Preferences', 'Additional Notes']
    const personalInformationForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                branch_ids: yup.array().of(yup.string()),
                branch_ids_helper: selectedBranches?.length > 0 ? yup.string().notRequired() : yup.string().required('Branches is required'),
                primary_branch_id: yup.string(),
                firstName: yup.string().required('First Name is required'),
                lastName: yup.string().required('Last Name is required'),
                maNumber: yup
                    .string()
                    .required('MA Number is required')
                    .matches(/^\d+$/, 'MA Number must contain only numbers')
                    .min(8, 'MA Number must be between 8 and 12 characters')
                    .max(12, 'MA Number must be between 8 and 12 characters'),
                gender: yup.string().required('Gender is required'),
                dateOfBirth: yup.date().required('Date of Birth is required'),
                primaryAddressStreetAddressLine1: yup.string().required('Address Line 1 is required'),
                primaryAddressStreetAddressLine2: yup.string(),
                primaryAddressCity: yup.string().required('City is required'),
                primaryAddressState: yup.string().required('State is required'),
                primaryAddressZipCode: yup
                    .string()
                    .required('Zip Code is required')
                    .min(5, 'Zip Code must be between 5 and 20 characters')
                    .max(20, 'Zip Code must be between 5 and 20 characters')
                    .matches(/^\d+$/, 'Zip Code must contain only numbers'),
                primaryAddressLatitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
                primaryAddressLongitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
                sameAsTheAddressAbove: yup.boolean().default(false),
                evvAddressStreetAddressLine1: yup.string().required('Address Line 1 is required'),
                evvAddressStreetAddressLine2: yup.string(),
                evvAddressCity: yup.string().required('City is required'),
                evvAddressState: yup.string().required('State is required'),
                evvAddressZipCode: yup
                    .string()
                    .required('Zip Code is required')
                    .min(5, 'Zip Code must be between 5 and 20 characters')
                    .max(20, 'Zip Code must be between 5 and 20 characters')
                    .matches(/^\d+$/, 'Zip Code must contain only numbers'),
                evvAddressLatitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
                evvAddressLongitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
            }),
        ),
    })
    const contactForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                primaryPhoneNumberType: yup.string().default('mobile'),
                primaryPhoneNumber: yup
                    .string()
                    .required('Phone number is required')
                    .test('is-valid-phone', 'Invalid phone number', value => phoneRegex.test(value)),
                primaryEmailAddressType: yup.string().default('personal'),
                primaryEmailAddress: yup
                    .string()
                    .required('Email is required')
                    .test('is-valid-email', 'Invalid email address', value => emailRegex.test(value)),
                primaryPhoneNumbers: yup.array().of(
                    yup.object().shape({
                        type: yup.string().default('mobile'),
                        number: yup.string().test('phone-optional', 'Invalid phone number', val => {
                            if (!val || val.trim() === '') return true
                            return phoneRegex.test(val)
                        }),
                    }),
                ),
                primaryEmailAddresses: yup.array().of(
                    yup.object().shape({
                        type: yup.string().default('personal'),
                        email: yup.string().test('email-optional', 'Invalid email address', val => {
                            if (!val || val.trim() === '') return true
                            return emailRegex.test(val)
                        }),
                    }),
                ),
                emergencyName: yup.string(),
                emergencyPhoneNumber: yup.string().test('phone-optional', 'Invalid phone number', value => {
                    if (!value || value.trim() === '') return true
                    return phoneRegex.test(value)
                }),
                emergencyEmailAddress: yup.string().test('email-optional', 'Invalid email address', value => {
                    if (!value || value.trim() === '') return true
                    return emailRegex.test(value)
                }),
                emergencyRelationship: yup.string(),
                emergencyAddressStreetAddressLine1: yup.string(),
                emergencyAddressStreetAddressLine2: yup.string(),
                emergencyAddressCity: yup.string(),
                emergencyAddressState: yup.string(),
                emergencyAddressZipCode: yup.string().test('zipcode-optional', 'Zip Code must be between 5 and 20 digits', value => {
                    if (!value || value.trim() === '') return true
                    return /^\d{5,20}$/.test(value)
                }),
                referralSource: yup.string(),
                referralPerson: yup.string(),
                referralEmail: yup.string().test('email-optional', 'Invalid email address', value => {
                    if (!value || value.trim() === '') return true
                    return emailRegex.test(value)
                }),
                referralPhoneNumber: yup.string().test('phone-optional', 'Invalid phone number', value => {
                    if (!value || value.trim() === '') return true
                    return phoneRegex.test(value)
                }),
            }),
        ),
    })
    const preferencesForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                hasDog: yup.boolean().default(false),
                hasCat: yup.boolean().default(false),
                hasPetCareAssistance: yup.boolean().default(false),
                man: yup.boolean().default(false),
                woman: yup.boolean().default(false),
                fullTime: yup.boolean().default(false),
                partTime: yup.boolean().default(false),
                liveIn: yup.boolean().default(false),
                hourlyShifts: yup.boolean().default(false),
                languages: yup.array().of(yup.string()),
                smokerInHoushold: yup.boolean().default(false),
                driving: yup.boolean().default(false),
                woundCare: yup.boolean().default(false),
                physicalTherapy: yup.boolean().default(false),
                requiresHoyerLiftAssistance: yup.boolean().default(false),
            }),
        ),
    })
    const additionalNotesForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                internalNote: yup.string(),
            }),
        ),
    })

    useEffect(() => {
        accountApi.getBranches().then(
            ({
                data: {
                    data: {branches},
                },
            }) => {
                setBranches(branches)
            },
        )
    }, [])

    useEffect(() => {
        if (activeStep == 3) {
            setNextLabel('Add Client')
        } else {
            setNextLabel('Next')
        }
    }, [activeStep])

    useEffect(() => {
        if (additionalNotesData) {
            submit()
        }
    }, [additionalNotesData])

    const previous = () => {
        setActiveStep(prev => --prev)
    }

    const next = () => {
        if (activeStep == 0) {
            personalInformationForm.handleSubmit(data => {
                setPersonalInformationData(data)
                setActiveStep(prev => ++prev)
            })()
        } else if (activeStep == 1) {
            contactForm.handleSubmit(data => {
                setContactData(data)
                setActiveStep(prev => ++prev)
            })()
        } else if (activeStep == 2) {
            preferencesForm.handleSubmit(data => {
                setPreferencesData(data)
                setActiveStep(prev => ++prev)
            })()
        } else if (activeStep == 3) {
            additionalNotesForm.handleSubmit(data => {
                setAdditionalNotesData(data)
            })()
        }
    }

    // Resolves to whether the photo made it; never rejects.
    const uploadPhoto = async clientId => {
        if (!file) return true
        const formData = new FormData()
        formData.append('file', file)
        try {
            await clientApi.uploadClientPhoto(clientId, formData)
            return true
        } catch {
            return false
        }
    }

    const submit = async () => {
        try {
            setLoading(true)
            const response = await clientApi.createBranchClient(branchId, {
                ma_number: personalInformationData?.maNumber,
                first_name: personalInformationData?.firstName,
                last_name: personalInformationData?.lastName,
                date_of_birth: personalInformationData?.dateOfBirth ? format(new Date(personalInformationData?.dateOfBirth), 'yyyy-MM-dd') : '',
                gender: personalInformationData?.gender,
                phone: contactData?.primaryPhoneNumber,
                email: contactData?.primaryEmailAddress,
                status: 'active',
                internal_notes: additionalNotesData?.internalNote,
                branch_ids: personalInformationData?.branch_ids,
                primary_branch_id: personalInformationData?.primary_branch_id,
            })

            const clientId = response?.data?.data?.id

            await clientApi.createClientAddress(clientId, {
                type: 'primary',
                street_line1: personalInformationData?.primaryAddressStreetAddressLine1,
                street_line2: personalInformationData?.primaryAddressStreetAddressLine2,
                city: personalInformationData?.primaryAddressCity,
                state: personalInformationData?.primaryAddressState,
                zip_code: personalInformationData?.primaryAddressZipCode,
                latitude: personalInformationData?.primaryAddressLatitude,
                longitude: personalInformationData?.primaryAddressLongitude,
            })
            await clientApi.createClientAddress(clientId, {
                type: 'evv_location',
                street_line1: personalInformationData?.evvAddressStreetAddressLine1,
                street_line2: personalInformationData?.evvAddressStreetAddressLine2,
                city: personalInformationData?.evvAddressCity,
                state: personalInformationData?.evvAddressState,
                zip_code: personalInformationData?.evvAddressZipCode,
                latitude: personalInformationData?.evvAddressLatitude,
                longitude: personalInformationData?.evvAddressLongitude,
            })

            let primaryPhoneNumbers = []
            contactData?.primaryPhoneNumbers?.map(item =>
                primaryPhoneNumbers.push({
                    type: item?.type,
                    number: item?.number,
                    is_primary: false,
                }),
            )
            let primaryEmailAddresses = []
            contactData?.primaryEmailAddresses?.map(item =>
                primaryEmailAddresses.push({
                    type: item?.type,
                    email: item?.email,
                    is_primary: false,
                }),
            )
            await clientApi.createClientContact(clientId, {
                type: 'primary',
                name: personalInformationData?.firstName + ' ' + personalInformationData?.lastName,
                phones: [
                    {
                        type: contactData?.primaryPhoneNumberType,
                        number: contactData?.primaryPhoneNumber,
                        is_primary: true,
                    },
                    ...primaryPhoneNumbers,
                ],
                emails: [
                    {
                        type: contactData?.primaryEmailAddressType,
                        email: contactData?.primaryEmailAddress,
                        is_primary: true,
                    },
                    ...primaryEmailAddresses,
                ],
            })

            if (contactData?.emergencyName) {
                await clientApi.createClientContact(clientId, {
                    type: 'emergency',
                    name: contactData?.emergencyName,
                    relationship: contactData?.emergencyRelationship,
                    phones: [
                        {
                            type: 'mobile',
                            number: contactData?.emergencyPhoneNumber,
                            is_primary: true,
                        },
                    ],
                    emails: [
                        {
                            type: 'personal',
                            email: contactData?.emergencyEmailAddress,
                            is_primary: true,
                        },
                    ],
                    addresses: [
                        {
                            type: 'home',
                            street_line1: contactData?.emergencyAddressStreetAddressLine1,
                            street_line2: contactData?.emergencyAddressStreetAddressLine2,
                            city: contactData?.emergencyAddressCity,
                            state: contactData?.emergencyAddressState,
                            zip_code: contactData?.emergencyAddressZipCode,
                            is_primary: true,
                        },
                    ],
                })
            }

            await clientApi.updateClientPreferences(clientId, {
                languages: preferencesData?.languages,
                has_dog: preferencesData?.hasDog,
                has_cat: preferencesData?.hasCat,
                has_pet_care_assistance: preferencesData?.hasPetCareAssistance,
                prefers_male: preferencesData?.man,
                prefers_female: preferencesData?.woman,
                no_gender_preference: !preferencesData?.man && !preferencesData?.woman,
                needs_full_time: preferencesData?.fullTime,
                needs_part_time: preferencesData?.partTime,
                needs_live_in: preferencesData?.liveIn,
                needs_hourly_shifts: preferencesData?.hourlyShifts,
                smoker_in_household: preferencesData?.smokerInHoushold,
                requires_driving: preferencesData?.driving,
                requires_wound_care: preferencesData?.woundCare,
                requires_physical_therapy: preferencesData?.physicalTherapy,
                requires_hoyer_lift_assistance: preferencesData?.requiresHoyerLiftAssistance,
            })
            // Last, and best-effort: the client is fully created by now, so a
            // failed photo must not send the user back to submit again, which
            // would create a duplicate. The photo can be added from the profile.
            const photoUploaded = await uploadPhoto(clientId)
            setLoading(false)

            setMessageBox(
                photoUploaded
                    ? {
                          status: 'success',
                          title: 'Client Added Successfully',
                          description: `${
                              personalInformationData?.firstName
                          } has been added to the system. You can now complete their profile, assign services, and manage their care journey.`,
                      }
                    : {
                          status: 'warning',
                          title: 'Client Added Without Photo',
                          description: `${personalInformationData?.firstName} has been added, but the photo couldn't be uploaded. You can add it from their profile.`,
                      },
            )
            navigate('/clients-management')
        } catch (error) {
            setLoading(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <Link to="/clients-management">
                            <IconButton>
                                <ArrowBack />
                            </IconButton>
                        </Link>
                        <Typography variant="h6" color="text.primary">
                            Add New Client
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" onClick={() => navigate('/clients-management')} style={{flex: 1}}>
                            Cancel
                        </Button>
                        {activeStep != 0 && (
                            <Button variant="outlined" onClick={previous} style={{flex: 1}}>
                                Previous
                            </Button>
                        )}
                        <Button variant="contained" onClick={next} style={{flex: 1}}>
                            {nextLabel}
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        <Link className="link" to="/clients-management">
                            Clients Management
                        </Link>
                        {' / '} Add New Client
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <Link className="back-button" to="/clients-management">
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </Link>
                            <Typography variant="h5" color="text.primary">
                                Add New Client
                            </Typography>
                        </div>
                        <div className="actions">
                            <Button variant="outlined" onClick={() => navigate('/clients-management')}>
                                Cancel
                            </Button>
                            {activeStep != 0 && (
                                <Button variant="outlined" onClick={previous}>
                                    Previous
                                </Button>
                            )}
                            <Button variant="contained" onClick={next}>
                                {nextLabel}
                            </Button>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            {mobile ? (
                <>
                    <Tabs value={activeStep} onChange={(e, newValue) => setActiveStep(newValue)} variant="scrollable">
                        <Tab label="Personal Information" disabled />
                        <Tab label="Contact" disabled />
                        <Tab label="Preferences" disabled />
                        <Tab label="Additional Info" disabled />
                    </Tabs>
                    <br />
                    <br />
                    <div style={{display: activeStep != 0 ? 'none' : 'block'}}>
                        <PersonalInformation
                            form={personalInformationForm}
                            avatarUrl={avatarUrl}
                            setAvatarUrl={setAvatarUrl}
                            file={file}
                            setFile={setFile}
                            branches={branches}
                            selectedBranches={selectedBranches}
                            setSelectedBranches={setSelectedBranches}
                        />
                    </div>
                    <div style={{display: activeStep != 1 ? 'none' : 'block'}}>
                        <Contact form={contactForm} />
                    </div>
                    <div style={{display: activeStep != 2 ? 'none' : 'block'}}>
                        <Preferences form={preferencesForm} />
                    </div>
                    <div style={{display: activeStep != 3 ? 'none' : 'block'}}>
                        <AdditionalNotes form={additionalNotesForm} />
                    </div>
                </>
            ) : (
                <div className="container">
                    <div className="step">
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={label} completed={index < activeStep}>
                                    <StepLabel
                                        slots={{
                                            stepIcon: ({active, completed, icon}) =>
                                                completed ? (
                                                    <CheckCircle color="primary" style={{fontSize: 28}} />
                                                ) : (
                                                    <Box
                                                        className="step-icon"
                                                        sx={theme => ({
                                                            backgroundColor: active ? theme.palette.primary.main : theme.palette.text.disabled,
                                                        })}>
                                                        {icon}
                                                    </Box>
                                                ),
                                        }}>
                                        <Typography variant="subtitle2">{label}</Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>
                    <div className="content">
                        {activeStep == 0 && (
                            <PersonalInformation
                                form={personalInformationForm}
                                avatarUrl={avatarUrl}
                                setAvatarUrl={setAvatarUrl}
                                file={file}
                                setFile={setFile}
                                branches={branches}
                                selectedBranches={selectedBranches}
                                setSelectedBranches={setSelectedBranches}
                            />
                        )}
                        {activeStep == 1 && <Contact form={contactForm} />}
                        {activeStep == 2 && <Preferences form={preferencesForm} />}
                        {activeStep == 3 && <AdditionalNotes form={additionalNotesForm} />}
                    </div>
                </div>
            )}
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
        </>
    )
}
